import React from 'react';
import styled from 'styled-components';

const ErrorContainer = styled.div`
  padding: 24px;
  border: 1px solid #ff6b6b;
  border-radius: 4px;
  background-color: #fff5f5;
  text-align: center;
  margin: 16px 0;
`;

const ErrorTitle = styled.h3`
  color: #d63031;
  margin: 0 0 12px 0;
  font-size: 18px;
  font-weight: 600;
`;

const ErrorMessage = styled.p`
  color: #636e72;
  margin: 0 0 16px 0;
  font-size: 14px;
  line-height: 1.5;
`;

const ErrorButton = styled.button`
  padding: 8px 16px;
  background-color: #0984e3;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;

  &:hover {
    background-color: #0056b3;
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

interface ErrorFallbackProps {
  title?: string;
  message?: string;
  onRetry?: () => void;
  retryLabel?: string;
  showRetry?: boolean;
}

export const ErrorFallback: React.FC<ErrorFallbackProps> = ({
  title = 'Something went wrong',
  message = 'An unexpected error occurred. Please try again.',
  onRetry,
  retryLabel = 'Try Again',
  showRetry = true,
}) => {
  return (
    <ErrorContainer>
      <ErrorTitle>{title}</ErrorTitle>
      <ErrorMessage>{message}</ErrorMessage>
      {showRetry && onRetry && <ErrorButton onClick={onRetry}>{retryLabel}</ErrorButton>}
    </ErrorContainer>
  );
};

export const TableErrorFallback: React.FC<{ onRetry?: () => void }> = ({ onRetry }) => (
  <ErrorFallback
    title="Unable to load transactions"
    message="There was a problem loading the transaction data. Please check your connection and try again."
    onRetry={onRetry}
    retryLabel="Reload Transactions"
  />
);

export const FiltersErrorFallback: React.FC<{ onRetry?: () => void }> = ({ onRetry }) => (
  <ErrorFallback
    title="Filter options unavailable"
    message="The filter options could not be loaded. You can still view transactions, but filtering may not work properly."
    onRetry={onRetry}
    retryLabel="Reload Filters"
  />
);

export const StatusBarErrorFallback: React.FC<{ onRetry?: () => void }> = ({ onRetry }) => (
  <ErrorFallback
    title="Status information unavailable"
    message="Transaction status information could not be displayed."
    onRetry={onRetry}
    showRetry={false}
  />
);
